import React, { useState, useEffect } from "react";
import logger from '../utils/logger';
import { useNavigate } from "react-router-dom";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { usePageTracking } from "../hooks/useAnalytics";
import useUserStorybooks from "../hooks/useUserStorybooks";

import { showToast } from "../components/Toast";
import CharacterStage from "../components/library/CharacterStage";
import FilmStrip from "../components/library/FilmStrip";
import StoryShelf from "../components/library/PictureBook";

function isFailedStory(story) {
  return (
    story.status === "FAILED" ||
    story.status === "failed" ||
    (story.status === "GENERATED" && (story.status_percentage || 0) < 100)
  );
}

const Library = () => {
  usePageTracking('Library');
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { user } = useAuth();
  const { currentProfile } = useProfile();
  const { books: storybooks, loading: storybooksLoading } = useUserStorybooks();
  const [characters, setCharacters] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllStories, setShowAllStories] = useState(false);
  const [showAllStorybooks, setShowAllStorybooks] = useState(false);
  const [showAllCharacters, setShowAllCharacters] = useState(false);
  // Rotate the header set per render (not a carousel). #2 is excluded. The
  // foreground images are normalized to a uniform 3:1 frame (characters flush to
  // the bottom with top headroom), so a single cover/bottom rule works for all.
  const HEADER_OPTIONS = [1, 3, 4];
  const [bannerIndex] = useState(
    () => HEADER_OPTIONS[Math.floor(Math.random() * HEADER_OPTIONS.length)]
  );

  useEffect(() => {
    loadLibraryData();
  }, [apiClient, user?.id, currentProfile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for character/story deletion events from detail pages
  useEffect(() => {
    if (!apiClient || !user?.id) return;

    const handleCharacterDeleted = (event) => {
      const { characterId } = event.detail || {};
      if (characterId) {
        // Optimistically remove from state
        // NOTE: Don't decrement count here - it's already decremented at the deletion source
        setCharacters(prev => prev.filter(char => char.id !== characterId));
        // Re-fetch in background to sync
        loadLibraryData().catch(err => {
          logger.error('Error re-fetching library after character deletion:', err);
        });
      }
    };

    const handleStoryDeleted = (event) => {
      const { storyId } = event.detail || {};
      if (storyId) {
        // Optimistically remove from state
        setStories(prev => prev.filter(story => story.id !== storyId));
        // Re-fetch in background to sync
        loadLibraryData().catch(err => {
          logger.error('Error re-fetching library after story deletion:', err);
        });
      }
    };

    window.addEventListener('character:deleted', handleCharacterDeleted);
    window.addEventListener('story:deleted', handleStoryDeleted);

    return () => {
      window.removeEventListener('character:deleted', handleCharacterDeleted);
      window.removeEventListener('story:deleted', handleStoryDeleted);
    };
  }, [apiClient, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track stories we've already sent completion emails for (prevent duplicates)
  const emailedStoriesRef = React.useRef(new Set());

  // Poll for story updates if any are processing
  useEffect(() => {
    const processingStories = stories.filter(story => {
      if (isFailedStory(story)) return false;
      const isProcessing = story.status === 'PROCESSING' ||
                          story.status === 'requested' ||
                          story.status === 'pending' ||
                          story.status === 'REQUESTED' ||
                          story.status === 'PENDING' ||
                          (!story.file_url && story.status !== 'COMPLETED');
      return isProcessing;
    });

    if (processingStories.length === 0) {
      logger.log('✅ No processing stories, skipping poll');
      return;
    }

    logger.log(`⏱️ Setting up polling for ${processingStories.length} processing stories`);

    const pollInterval = setInterval(async () => {
      logger.log('🔄 Polling for story updates in Library...');

      // Update only processing stories individually
      const updatePromises = processingStories.map(async (story) => {
        try {
          const response = await apiClient.getStory(story.id);
          if (response.status && response.data) {
            return response.data;
          }
          return null;
        } catch (error) {
          logger.error(`Error polling story ${story.id}:`, error);
          return null;
        }
      });

      const updatedStories = await Promise.all(updatePromises);

      // Check for newly completed stories and send email notifications (with deduplication)
      updatedStories.forEach((updatedStory, index) => {
        if (!updatedStory) return;

        // Email notification is now handled by video driver on completion
      });

      // Update only the stories that were successfully fetched
      setStories(prevStories =>
        prevStories.map(story => {
          const updatedStory = updatedStories.find(updated => updated && updated.id === story.id);
          return updatedStory || story;
        })
      );
    }, 5000); // Poll every 5 seconds (reduced from 2s for better performance)

    return () => {
      logger.log('🛑 Cleaning up story polling interval');
      clearInterval(pollInterval);
    };
  }, [stories.length, apiClient]); // Only re-run when count changes, not on every story update

  const loadLibraryData = async () => {
    if (!apiClient || !user?.id) return;

    try {
      setLoading(true);

      // Load characters and stories for current profile
      logger.log(user);
      const [charactersResponse, storiesResponse] = await Promise.all([
        apiClient.getUserCharacters(user.id, currentProfile?.id),
        apiClient.getUserStories(currentProfile?.id),
      ]);

      if (charactersResponse.status) {
        const charactersData = Array.isArray(charactersResponse.data)
          ? charactersResponse.data
          : [];
        setCharacters(charactersData);
      }

      if (storiesResponse.status) {
        const storiesData = storiesResponse.data || [];
        setStories(storiesData);
      }
    } catch (error) {
      logger.error("Failed to load library:", error);
      showToast("Failed to load library", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Greeting based on local time ──
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning,";
    if (h < 17) return "Good afternoon,";
    return "Good evening,";
  })();
  const displayName = user?.username || currentProfile?.name || "friend";

  const FREDOKA_STYLE = { fontFamily: "'Fredoka', sans-serif" };

  if (loading) {
    return (
      <div
        className="library-page-container min-h-screen relative"
        style={{
          background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
          fontFamily: "'Inter', 'Fredoka', sans-serif",
        }}
      >
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24 pb-16">
          {/* Banner skeleton */}
          <div
            className="animate-pulse w-full"
            style={{
              height: "clamp(200px, 28vw, 340px)",
              background: "rgba(11,26,51,0.06)",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRadius: 28,
            }}
          />
          {/* Section skeletons */}
          {[0, 1, 2].map((s) => (
            <div key={s} className="mt-10">
              <div className="h-7 w-44 mb-4 animate-pulse" style={{ background: "rgba(11,26,51,0.08)", borderRadius: 12 }} />
              <div className="flex gap-4 overflow-hidden">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse shrink-0"
                    style={{
                      width: "clamp(120px, 14vw, 160px)",
                      height: "clamp(160px, 20vw, 220px)",
                      background: "rgba(11,26,51,0.06)",
                      borderRadius: 12,
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="library-page-container min-h-screen relative"
      style={{
        background: "#EEF4FF",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
        paddingBottom: 1, // prevent margin-collapse exposing dark App bg
        overflowX: "hidden", // contain the full-bleed banner's 100vw breakout
      }}
    >
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          style={{
            position: "absolute",
            top: -180,
            left: -160,
            width: 480,
            height: 480,
            borderRadius: "50%",
            background: "rgba(0,101,248,0.08)",
            filter: "blur(30px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -200,
            right: -200,
            width: 520,
            height: 520,
            borderRadius: "50%",
            background: "rgba(0,202,255,0.08)",
            filter: "blur(30px)",
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Full-bleed rotating hero banner (one of 4 images per render).
            Breaks out to the full viewport width and fades into the page on
            every edge, so it sits flush in the page rather than as a card. */}
        <section
          className="relative"
          style={{
            width: "100vw",
            marginLeft: "calc(50% - 50vw)",
            height: "clamp(360px, 40vw, 560px)",
          }}
        >
          {/* Layered banner: solid to all edges (no top fade). The content
              panel below handles the bottom blend; the nav floats over the top. */}
          <div className="absolute inset-0">
            {/* Background scene — fills the banner; cropping/stretching is
                invisible here since it's just scenery. */}
            <img
              src={`/assets/library-header-${bannerIndex}-bg.png`}
              alt=""
              className="absolute inset-0 w-full h-full"
              style={{ objectFit: "cover", objectPosition: "center" }}
            />
            {/* Soft wash for greeting legibility — sits over the bg but UNDER the
                characters, so they stay bright. */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(125% 95% at 0% 100%, rgba(11,26,51,0.55) 0%, rgba(11,26,51,0.18) 30%, rgba(11,26,51,0) 56%)",
              }}
            />
            {/* Foreground characters — natural proportions (no stretch), lifted
                off the bottom so they sit in the scene (not flush at the edge).
                The clear left side leaves room for the greeting. */}
            <img
              src={`/assets/library-header-${bannerIndex}-fg.png`}
              alt=""
              className="absolute inset-0 w-full h-full"
              style={{ objectFit: "cover", objectPosition: "center bottom" }}
            />
          </div>
          {/* Greeting — sits above the feathered bottom, aligned to content width */}
          <div className="absolute inset-x-0 bottom-0">
            <div
              className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8"
              style={{ paddingBottom: "clamp(48px, 7vw, 86px)" }}
            >
              <h1
                style={{
                  ...FREDOKA_STYLE,
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "clamp(1.5rem, 3.4vw, 2.4rem)",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.05,
                  margin: 0,
                  textShadow: "0 2px 18px rgba(11,26,51,0.7), 0 1px 4px rgba(11,26,51,0.55)",
                }}
              >
                {greeting} {displayName}! 👋
              </h1>
              <p
                style={{
                  ...FREDOKA_STYLE,
                  color: "rgba(255,255,255,0.96)",
                  fontSize: "clamp(13px, 1.5vw, 16px)",
                  fontWeight: 500,
                  margin: "6px 0 0",
                  textShadow: "0 2px 14px rgba(11,26,51,0.75), 0 1px 3px rgba(11,26,51,0.55)",
                }}
              >
                Your characters can't wait for their next adventure.
              </p>
            </div>
          </div>
        </section>

        {/* Content surface — rises over the banner with a rounded top so the
            sections tuck under it and blend (text never lands on the image). */}
        <div
          className="relative"
          style={{
            background: "#EEF4FF",
            borderTopLeftRadius: 30,
            borderTopRightRadius: 30,
            marginTop: "clamp(-36px, -2.6vw, -20px)",
            paddingTop: "clamp(26px, 3vw, 40px)",
            paddingBottom: 64,
            boxShadow: "0 -10px 30px rgba(11,26,51,0.07)",
          }}
        >
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* YOUR STORIES */}
        <section className="mb-6">
          <div className="flex items-end justify-between mb-4">
            <h2
              style={{
                ...FREDOKA_STYLE,
                color: "#0065F8",
                fontWeight: 700,
                fontSize: "clamp(20px, 2.4vw, 26px)",
                letterSpacing: "-0.01em",
                margin: 0,
              }}
            >
              Your Stories
            </h2>
            {stories.length > 5 && (
              <button
                type="button"
                onClick={() => setShowAllStories((v) => !v)}
                className="inline-flex items-center gap-1 transition"
                style={{
                  ...FREDOKA_STYLE,
                  color: "#0065F8",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {showAllStories ? "Show less" : `View all (${stories.length})`}
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  viewBox="0 0 24 24"
                  style={{
                    transition: "transform 200ms",
                    transform: showAllStories ? "rotate(-90deg)" : "rotate(90deg)",
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
                </svg>
              </button>
            )}
          </div>

          <FilmStrip
            stories={stories}
            expanded={showAllStories}
            onStoryClick={(s) => {
              sessionStorage.setItem("autoplay_video_story", "true");
              navigate(`/stories/${s.id}`);
            }}
            onCreate={() => navigate("/create")}
          />
        </section>

        {/* YOUR STORYBOOKS */}
        <section className="mb-6">
          <div className="flex items-end justify-between mb-4">
            <h2
              style={{
                ...FREDOKA_STYLE,
                color: "#0065F8",
                fontWeight: 700,
                fontSize: "clamp(20px, 2.4vw, 26px)",
                letterSpacing: "-0.01em",
                margin: 0,
              }}
            >
              Your Storybooks
            </h2>
            {storybooks.length > 5 && (
              <button
                type="button"
                onClick={() => setShowAllStorybooks((v) => !v)}
                className="inline-flex items-center gap-1 transition"
                style={{
                  ...FREDOKA_STYLE,
                  color: "#0065F8",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {showAllStorybooks ? "Show less" : `View all (${storybooks.length})`}
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  viewBox="0 0 24 24"
                  style={{
                    transition: "transform 200ms",
                    transform: showAllStorybooks ? "rotate(-90deg)" : "rotate(90deg)",
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
                </svg>
              </button>
            )}
          </div>

          {storybooksLoading ? (
            <div className="flex gap-4 overflow-hidden pt-6">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse shrink-0"
                  style={{
                    width: "clamp(120px, 14vw, 160px)",
                    height: "clamp(160px, 20vw, 220px)",
                    background: "rgba(11,26,51,0.06)",
                    borderRadius: 12,
                  }}
                />
              ))}
            </div>
          ) : (
            <StoryShelf
              stories={storybooks}
              expanded={showAllStorybooks}
              onStoryClick={(book) => navigate(`/audiobooks/${book.id}`)}
              onCreate={() => navigate("/storybook-template")}
              emptyTitle="No storybooks yet"
              emptySubtitle="Create an illustrated, read-aloud picture book."
              emptyCtaLabel="+ Create your first storybook"
            />
          )}
        </section>

        {/* YOUR CAST */}
        <section>
          <div className="flex items-end justify-between mb-4">
            <h2
              style={{
                ...FREDOKA_STYLE,
                color: "#0065F8",
                fontWeight: 700,
                fontSize: "clamp(20px, 2.4vw, 26px)",
                letterSpacing: "-0.01em",
                margin: 0,
              }}
            >
              Your Cast
            </h2>
            {characters.length > 6 && (
              <button
                type="button"
                onClick={() => setShowAllCharacters((v) => !v)}
                className="inline-flex items-center gap-1 transition"
                style={{
                  ...FREDOKA_STYLE,
                  color: "#0065F8",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {showAllCharacters ? "Show less" : `View all (${characters.length})`}
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  viewBox="0 0 24 24"
                  style={{
                    transition: "transform 200ms",
                    transform: showAllCharacters ? "rotate(-90deg)" : "rotate(90deg)",
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
                </svg>
              </button>
            )}
          </div>

          <CharacterStage
            characters={characters}
            stories={stories}
            expanded={showAllCharacters}
            onCharacterClick={(c) => navigate(`/characters/${c.id}`)}
            onCreate={() => navigate("/character-lab")}
          />
        </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Library;
